import type { Editor } from "@tiptap/react";
import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, Code, ImageIcon, Italic, Link2, List, ListOrdered, Quote, UnderlineIcon } from "lucide-react";
import EditorButton from "./editor-button";
import HeadingSelect from "./heading-select";


const Divider = () => <div className="w-px h-6 bg-zinc-700 mx-1" />;


export const MenuBar = ({ editor }: { editor: Editor | null }) => {
    if (!editor) return null;

    return (
        <div className="flex flex-wrap items-center gap-2 p-2 border-b border-zinc-800 bg-zinc-900/70 rounded-t-lg sticky top-16 z-50">
            {/* Estilo de texto */}
            <HeadingSelect editor={editor} />

            {/* Estilos inline */}
            <div className="flex items-center gap-1">
                <EditorButton
                    icon={<Bold className="w-4 h-4" />}
                    title="Negrito"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    isActive={editor.isActive("bold")}
                />
                <EditorButton
                    icon={<Italic className="w-4 h-4" />}
                    title="Itálico"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    isActive={editor.isActive("italic")}
                />
                <EditorButton
                    icon={<UnderlineIcon className="w-4 h-4" />}
                    title="Sublinhado"
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    isActive={editor.isActive("underline")}
                />
            </div>

            <Divider />

            {/* Listas e citações */}
            <div className="flex items-center gap-1">
                <EditorButton
                    icon={<List className="w-4 h-4" />}
                    title="Lista com marcadores"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    isActive={editor.isActive("bulletList")}
                />
                <EditorButton
                    icon={<ListOrdered className="w-4 h-4" />}
                    title="Lista numerada"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    isActive={editor.isActive("orderedList")}
                />
                <EditorButton
                    icon={<Quote className="w-4 h-4" />}
                    title="Citação"
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    isActive={editor.isActive("blockquote")}
                />
                <EditorButton
                    icon={<Code className="w-4 h-4" />}
                    title="Código"
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    isActive={editor.isActive("codeBlock")}
                />
            </div>

            <Divider />

            {/* Alinhamento */}
            <div className="flex items-center gap-1">
                <EditorButton
                    icon={<AlignLeft className="w-4 h-4" />}
                    title="Alinhar à esquerda"
                    onClick={() => editor.chain().focus().setTextAlign("left").run()}
                    isActive={editor.isActive({ textAlign: "left" })}
                />
                <EditorButton
                    icon={<AlignCenter className="w-4 h-4" />}
                    title="Centralizar"
                    onClick={() => editor.chain().focus().setTextAlign("center").run()}
                    isActive={editor.isActive({ textAlign: "center" })}
                />
                <EditorButton
                    icon={<AlignRight className="w-4 h-4" />}
                    title="Alinhar à direita"
                    onClick={() => editor.chain().focus().setTextAlign("right").run()}
                    isActive={editor.isActive({ textAlign: "right" })}
                />
                <EditorButton
                    icon={<AlignJustify className="w-4 h-4" />}
                    title="Justificar"
                    onClick={() => editor.chain().focus().setTextAlign("justify").run()}
                    isActive={editor.isActive({ textAlign: "justify" })}
                />
            </div>

            {/* <Divider /> */}

            {/* Links e Imagens */}
            <div className="flex items-center gap-1">
                <EditorButton
                    icon={<Link2 className="w-4 h-4" />}
                    title="Inserir link"
                    onClick={() => {
                        const previousUrl = editor.getAttributes("link").href;
                        const url = window.prompt("URL", previousUrl || "https://");
                        if (url === null) return;
                        if (url === "") {
                            editor.chain().focus().extendMarkRange("link").unsetLink().run();
                        } else {
                            editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
                        }
                    }}
                    isActive={editor.isActive("link")}
                />
                <EditorButton
                    icon={<ImageIcon className="w-4 h-4" />}
                    title="Inserir imagem"
                    onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                    const src = e.target?.result as string;
                                    editor.chain().focus().setImage({ src }).run();
                                };
                                reader.readAsDataURL(file);
                            }
                        };
                        input.click();
                    }}
                />
            </div>
        </div>
    );
};
